import { useEffect, useRef, useState } from 'react'
import { Loader2, Check } from 'lucide-react'
import { apiGet } from '../../lib/api.js'
import Pager from '../Pager.jsx'

// Read-only section chrome shared by the drawers under ติดตาม. Every one of these
// is prop-only and closes over nothing — they were inlined in RecallPopup.jsx and
// are moved here verbatim so the patient ติดตาม page reuses them instead of
// growing a second, subtly different set.
/* ── shared primitives ────────────────────────────────────────────────────── */
// one-shot loader for a read-only section; refetches when `key` changes. `tick`
// (optional) re-reads the SAME key quietly: the rows stay on screen while it loads,
// and a failed refresh keeps them rather than swapping in an error — a live update
// that hiccups must not blank a list the user is reading.
export function useSection(path, params, key, tick = 0) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const reqId = useRef(0)
  const loadedKey = useRef(undefined)
  useEffect(() => {
    if (key == null) return
    const id = ++reqId.current
    const fresh = loadedKey.current !== key
    loadedKey.current = key
    if (fresh) { setData(null); setErr('') }
    apiGet(path, params)
      .then((r) => { if (id === reqId.current) { setData(r ?? []); setErr('') } })
      // null, NOT [] — an errored list must read as "unknown", never as "empty".
      .catch((e) => { if (id === reqId.current && fresh) setErr(String(e.message || e)) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tick])
  return { data, err }
}

export function Section({ title, icon: Icon, count, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={15} className="text-slate-400" />}
        <h3 className="text-[13px] font-semibold text-slate-700">{title}</h3>
        {count != null && <span className="text-[11px] text-slate-400 bg-slate-100 rounded-full px-1.5 py-0.5">{count}</span>}
      </div>
      {children}
    </section>
  )
}
// Paginated read-only table. `renderRow(row)` returns the cell array. 10/page,
// page resets when the dataset (rows identity) changes — e.g. switching patient.
export function PagedTable({ data, err, head, rows, renderRow, empty, per = 10 }) {
  const [page, setPage] = useState(1)
  useEffect(() => { setPage(1) }, [rows])
  if (err) return <p className="text-[12px] text-amber-700">โหลดไม่สำเร็จ: {err}</p>
  if (data == null) return <Loading />
  if (!rows.length) return <Empty>{empty}</Empty>
  const pageTotal = Math.max(1, Math.ceil(rows.length / per))
  const cur = Math.min(page, pageTotal)
  const slice = rows.slice((cur - 1) * per, cur * per)
  return (
    <div>
      <div className="overflow-x-auto rounded-lg border border-slate-100">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-[11px] text-slate-400 bg-slate-50/70">
              {head.map((h) => <th key={h} className="text-left font-medium px-2.5 py-1.5 whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {slice.map((row, i) => (
              <tr key={i} className="border-t border-slate-50">
                {renderRow(row).map((c, j) => <td key={j} className="px-2.5 py-1.5 text-slate-700 whitespace-nowrap">{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pageTotal > 1 && (
        <div className="flex items-center justify-between mt-2 text-[11.5px] text-slate-500">
          <span>{rows.length} รายการ</span>
          <Pager page={cur} pageTotal={pageTotal} onChange={setPage} size="sm" />
        </div>
      )}
    </div>
  )
}
export function Pill({ label, cls }) {
  return <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10.5px] ring-1 ${cls || 'bg-slate-50 text-slate-600 ring-slate-200'}`}>{label}</span>
}
export function Empty({ children }) { return <p className="text-[12.5px] text-slate-400 py-1">{children}</p> }
export function Loading() { return <div className="flex items-center gap-2 text-[12.5px] text-slate-400 py-2"><Loader2 size={14} className="animate-spin" /> กำลังโหลด…</div> }

export function CopyText({ text, className = '' }) {
  const [copied, setCopied] = useState(false)
  return (
    <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(String(text)); setCopied(true); setTimeout(() => setCopied(false), 1200) } catch { /* ignore */ } }}
      className={`inline-flex items-center gap-1 hover:text-brand-600 ${className}`} title="คัดลอก">
      {text}{copied ? <Check size={11} className="text-emerald-500" /> : null}
    </button>
  )
}
export function ActBtn({ children, onClick, busy, tone = 'ghost', Icon }) {
  const tones = {
    brand: 'text-white bg-brand-600 hover:bg-brand-700',
    amber: 'text-white bg-amber-500 hover:bg-amber-600',
    ghost: 'text-slate-600 bg-white border border-slate-200 hover:bg-slate-50',
  }
  return (
    <button type="button" onClick={onClick} disabled={busy}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[12px] font-medium disabled:opacity-50 ${tones[tone]}`}>
      {Icon && <Icon size={13} className={busy ? 'animate-pulse' : ''} />} {children}
    </button>
  )
}
